import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCustomerFetch, Card, SectionLabel, StatusBadge, Sheet, EmptyState, daysBetween } from "../../components/customer-app/ui";
import { Sun, ShieldCheck, ShieldAlert, Cpu, Hash, Calendar, ChevronLeft, AlertTriangle } from "lucide-react";

const IB_HERO_PHOTO = "https://images.unsplash.com/photo-1620492948585-c97e18c173dc?w=500&q=80&auto=format&fit=crop";

const EQUIPMENT_PHOTOS = [
  { match: /panel|module/i, image: "https://images.unsplash.com/photo-1724041875334-0a6397111c7e?w=200&q=80&auto=format&fit=crop" },
  { match: /invert/i, image: "https://images.pexels.com/photos/33751638/pexels-photo-33751638.jpeg?w=200&q=80&auto=format&fit=crop" },
  { match: /batter/i, image: "https://images.pexels.com/photos/37929911/pexels-photo-37929911.jpeg?w=200&q=80&auto=format&fit=crop" },
  { match: /meter/i, image: "https://images.unsplash.com/photo-1792419766858-ecacd2034515?w=200&q=80&auto=format&fit=crop" },
];
function equipmentImage(materialName) {
  const hit = EQUIPMENT_PHOTOS.find((p) => p.match.test(materialName || ""));
  return (hit || EQUIPMENT_PHOTOS[0]).image;
}

export default function CustomerInstallations() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: ibs, loading } = useCustomerFetch("/customer-app/installed-base");
  const [detailId, setDetailId] = useState(params.get("open") || null);
  const { data: detail } = useCustomerFetch(detailId ? `/customer-app/installed-base/${detailId}` : null, [detailId]);

  // Deep-linked from a ticket's "Installation Completed" banner via ?open=<ib_id>
  useEffect(() => {
    const openId = params.get("open");
    if (openId) setDetailId(openId);
  }, [params]);

  function closeDetail() {
    setDetailId(null);
    if (params.get("open")) {
      params.delete("open");
      setParams(params, { replace: true });
    }
  }

  return (
    <div className="space-y-3 pb-4" data-testid="customer-installations">
      <button onClick={() => nav(-1)} data-testid="installations-back-btn" className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 mb-1">
        <ChevronLeft className="w-3.5 h-3.5" /> Back
      </button>
      <SectionLabel>My Installations</SectionLabel>

      {loading && <div className="h-28 rounded-2xl bg-slate-100 animate-pulse" />}

      {!loading && ibs?.length === 0 && (
        <EmptyState icon={Sun} title="No installations yet" subtitle="Once your system is commissioned, it'll show up here." />
      )}

      {!loading && ibs?.map((ib) => {
        const elapsed = daysBetween(ib.commissioning_date);
        const total = elapsed + Math.max(ib.days_left, 0);
        const pct = total > 0 ? Math.min(100, Math.round((elapsed / total) * 100)) : 0;
        return (
          <button key={ib.id} onClick={() => setDetailId(ib.id)} data-testid={`ib-card-${ib.ib_number}`} className="w-full text-left">
            <Card className="overflow-hidden">
              <div className="relative h-20">
                <img src={IB_HERO_PHOTO} alt="Rooftop solar installation" className="w-full h-full object-cover" draggable={false} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                <div className="absolute left-3 bottom-2 text-[10px] font-mono uppercase tracking-widest text-white/90">{ib.ib_number}</div>
              </div>
              <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-base font-bold text-slate-800">
                    {ib.equipment_count} Equipment{ib.equipment_count !== 1 ? "s" : ""} Installed
                  </div>
                </div>
                <StatusBadge tone={ib.is_active ? "live_healthy" : "action_required"}>
                  {ib.is_active ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                  {ib.is_active ? "Under Warranty" : "Expired"}
                </StatusBadge>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-3">
                <Calendar className="w-3.5 h-3.5" /> Installed {new Date(ib.commissioning_date).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1.5" data-testid={`ib-card-warranty-end-${ib.ib_number}`}>
                <ShieldCheck className="w-3.5 h-3.5" /> Warranty End Date: {ib.expiry_date ? new Date(ib.expiry_date).toLocaleDateString() : "—"}
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-[11px] font-mono text-slate-500 mb-1">
                  <span>{ib.is_active ? "In Warranty" : "Out of Warranty"}</span>
                  <span>{ib.is_active ? `${ib.days_left}d left` : `Expired ${Math.abs(ib.days_left)}d ago`}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={"h-full rounded-full " + (ib.is_active ? "bg-emerald-500" : "bg-red-400")} style={{ width: `${pct}%` }} />
                </div>
              </div>
              {(ib.opportunity_code || ib.ticket_number || ib.quotation_number) && (
                <div className="flex flex-wrap gap-1.5 mt-3 text-[10px] font-mono text-slate-400">
                  {ib.opportunity_code && <span className="bg-slate-100 rounded-md px-1.5 py-0.5">{ib.opportunity_code}</span>}
                  {ib.ticket_number && <span className="bg-slate-100 rounded-md px-1.5 py-0.5">{ib.ticket_number}</span>}
                  {ib.quotation_number && <span className="bg-slate-100 rounded-md px-1.5 py-0.5">{ib.quotation_number}</span>}
                </div>
              )}
              </div>
            </Card>
          </button>
        );
      })}

      <Sheet open={!!detailId} onClose={closeDetail} title={detail?.ib_number || "Installation"} testid="ib-detail-sheet">
        {!detail ? (
          <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 bg-slate-50 rounded-xl p-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Date of Installation</div>
                <div className="text-sm font-semibold text-slate-800 mt-0.5">{new Date(detail.commissioning_date).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Warranty End Date</div>
                <div className="text-sm font-semibold text-slate-800 mt-0.5" data-testid="ib-detail-warranty-end">
                  {detail.warranty_valid_date ? new Date(detail.warranty_valid_date).toLocaleDateString() : (detail.comprehensive_warranty.expiry_date ? new Date(detail.comprehensive_warranty.expiry_date).toLocaleDateString() : "—")}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Warranty Status</div>
                <div className={"text-sm font-semibold mt-0.5 " + (detail.comprehensive_warranty.is_active ? "text-emerald-700" : "text-red-600")} data-testid="ib-detail-warranty-status">
                  {detail.comprehensive_warranty.is_active ? `In Warranty · ${detail.comprehensive_warranty.days_left}d left` : `Out of Warranty · expired ${Math.abs(detail.comprehensive_warranty.days_left)}d ago`}
                </div>
              </div>
              {detail.opportunity_code && <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Opportunity</div><div className="text-sm font-mono font-semibold text-slate-800 mt-0.5">{detail.opportunity_code}</div></div>}
              {detail.ticket_number && <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Ticket Ref</div><div className="text-sm font-mono font-semibold text-slate-800 mt-0.5">{detail.ticket_number}</div></div>}
              {detail.quotation_number && <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Quotation</div><div className="text-sm font-mono font-semibold text-slate-800 mt-0.5">{detail.quotation_number}</div></div>}
            </div>

            <div>
              <SectionLabel className="mb-2 flex items-center gap-1.5"><Cpu className="w-3 h-3" /> Equipment ({detail.equipment.length})</SectionLabel>
              <div className="space-y-2">
                {detail.equipment.map((eq) => (
                  <div key={eq.id} data-testid={`equipment-row-${eq.serial_code}`} className={"border rounded-xl overflow-hidden " + (eq.is_active ? "border-slate-200" : "border-red-300")}>
                    {!eq.is_active && (
                      <div className="bg-red-600 text-white text-[11px] font-extrabold px-3 py-1.5 flex items-center gap-1.5" data-testid={`equipment-out-of-warranty-${eq.serial_code}`}>
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {eq.material_name} — Out of Warranty
                      </div>
                    )}
                    <div className="p-3 flex gap-2.5">
                      <img src={equipmentImage(eq.material_name)} alt={eq.material_name} className="w-11 h-11 rounded-lg object-cover flex-shrink-0 border border-slate-200" draggable={false} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-bold text-slate-800">{eq.material_name}</div>
                            <div className="text-[11px] font-mono text-slate-400">{eq.material_code}{eq.description ? ` · ${eq.description}` : ""}</div>
                          </div>
                          {eq.is_active && <StatusBadge tone="live_healthy">{eq.days_left}d</StatusBadge>}
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] font-mono text-slate-500 mt-2">
                          <Hash className="w-3 h-3" /> {eq.serial_code}
                        </div>
                        <div className="text-[11px] font-mono text-slate-500 mt-1" data-testid={`equipment-warranty-end-${eq.serial_code}`}>
                          Warranty End: {eq.expiry_date ? new Date(eq.expiry_date).toLocaleDateString() : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}
